// ------------------------------------------------------------
// Canonical product visual geometry (picker + live canvas)
// size_px = maximum bounding-box dimension at 100% canvas zoom
// ------------------------------------------------------------

export const DEFAULT_PRODUCT_COLOR = '#ffcc00';
export const DEFAULT_PRODUCT_SHAPE = 'circle';
export const DEFAULT_PRODUCT_SIZE = 8;
export const MIN_PRODUCT_SIZE = 4;
export const MAX_PRODUCT_SIZE = 32;

export const PRODUCT_SHAPES = [
  'circle',
  'square',
  'rectangle',
  'triangle',
  'diamond',
] as const;

export type ProductShapeName = (typeof PRODUCT_SHAPES)[number];

export function normalizeShape(shape?: string | null): ProductShapeName {
  const value = (shape ?? DEFAULT_PRODUCT_SHAPE).toLowerCase();
  if (PRODUCT_SHAPES.includes(value as ProductShapeName)) {
    return value as ProductShapeName;
  }
  return DEFAULT_PRODUCT_SHAPE;
}

export function clampProductSize(size?: number | null): number {
  const value = size ?? DEFAULT_PRODUCT_SIZE;
  return Math.min(MAX_PRODUCT_SIZE, Math.max(MIN_PRODUCT_SIZE, value));
}

/** Map configured 4–32 px into compact 8–28 preview range. */
export function previewProductSize(sizePx: number): number {
  const clamped = clampProductSize(sizePx);
  const t = (clamped - MIN_PRODUCT_SIZE) / (MAX_PRODUCT_SIZE - MIN_PRODUCT_SIZE);
  return 8 + t * 20;
}

/** World-space draw size before stage zoom; multiply by zoom for on-screen pixels. */
export function canvasProductSize(
  sizePx: number,
  nodeDisplayScale: number,
  zoom: number,
): number {
  const base = clampProductSize(sizePx);
  const z = zoom > 0 ? zoom : 1;
  return base * (nodeDisplayScale / z);
}

/** Minimum world-space size so shapes remain drawable (does not flatten type ratios). */
export function drawBoxSize(sizePx: number): number {
  return Math.max(0.5, sizePx);
}

/** Effective on-screen size after the canvas stage applies zoom. */
export function screenProductSize(
  sizePx: number,
  nodeDisplayScale: number,
  zoom: number,
): number {
  const z = zoom > 0 ? zoom : 1;
  return canvasProductSize(sizePx, nodeDisplayScale, z) * z;
}

export interface ResolvedProductVisual {
  color: string;
  shape: ProductShapeName;
  size_px: number;
}

export function resolveProductVisual(input: {
  color?: string | null;
  shape?: string | null;
  size?: number | null;
  size_px?: number | null;
}): ResolvedProductVisual {
  return {
    color: input.color?.trim() ? input.color : DEFAULT_PRODUCT_COLOR,
    shape: normalizeShape(input.shape),
    size_px: clampProductSize(input.size_px ?? input.size),
  };
}

type DrawContext = {
  beginPath: () => void;
  closePath: () => void;
  moveTo: (x: number, y: number) => void;
  lineTo: (x: number, y: number) => void;
  rect: (x: number, y: number, w: number, h: number) => void;
  arc: (
    x: number,
    y: number,
    r: number,
    start: number,
    end: number,
  ) => void;
  fill: () => void;
  stroke: () => void;
  fillStyle: string | CanvasGradient | CanvasPattern;
  strokeStyle: string | CanvasGradient | CanvasPattern;
  globalAlpha: number;
  lineWidth: number;
};

export function drawProductShape(
  ctx: DrawContext,
  shape: string,
  sizePx: number,
  color: string,
  opacity = 1,
) {
  const box = drawBoxSize(sizePx);
  ctx.beginPath();
  ctx.fillStyle = color;
  ctx.globalAlpha = opacity;
  ctx.strokeStyle = '#1A1A1A';
  ctx.lineWidth = 0.75;

  switch (normalizeShape(shape)) {
    case 'square': {
      const half = box / 2;
      ctx.rect(-half, -half, box, box);
      break;
    }
    case 'rectangle': {
      const width = box;
      const height = box * 0.6;
      ctx.rect(-width / 2, -height / 2, width, height);
      break;
    }
    case 'triangle': {
      const radius = box / 2;
      for (let i = 0; i < 3; i += 1) {
        const angle = (i * 2 * Math.PI) / 3 - Math.PI / 2;
        const x = Math.cos(angle) * radius;
        const y = Math.sin(angle) * radius;
        if (i === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      }
      ctx.closePath();
      break;
    }
    case 'diamond': {
      const half = box / 2;
      ctx.moveTo(0, -half);
      ctx.lineTo(half, 0);
      ctx.lineTo(0, half);
      ctx.lineTo(-half, 0);
      ctx.closePath();
      break;
    }
    default: {
      ctx.arc(0, 0, box / 2, 0, Math.PI * 2);
      break;
    }
  }

  ctx.fill();
  ctx.stroke();
}

export function productShapeSvgPath(
  shape: string,
  box: number,
): { element: 'rect' | 'circle' | 'polygon'; attrs: Record<string, number | string> } {
  const size = Math.max(8, box);
  const stroke = Math.max(0.5, size * 0.04);
  const inset = size * 0.06;

  switch (normalizeShape(shape)) {
    case 'square':
      return {
        element: 'rect',
        attrs: {
          x: inset,
          y: inset,
          width: size - inset * 2,
          height: size - inset * 2,
          strokeWidth: stroke,
        },
      };
    case 'rectangle': {
      const width = size - inset * 2;
      const height = width * 0.6;
      const y = (size - height) / 2;
      return {
        element: 'rect',
        attrs: {
          x: inset,
          y,
          width,
          height,
          strokeWidth: stroke,
        },
      };
    }
    case 'triangle':
      return {
        element: 'polygon',
        attrs: {
          points: `${size / 2},${inset} ${size - inset},${size - inset} ${inset},${size - inset}`,
          strokeWidth: stroke,
        },
      };
    case 'diamond':
      return {
        element: 'polygon',
        attrs: {
          points: `${size / 2},${inset} ${size - inset},${size / 2} ${size / 2},${size - inset} ${inset},${size / 2}`,
          strokeWidth: stroke,
        },
      };
    default:
      return {
        element: 'circle',
        attrs: {
          cx: size / 2,
          cy: size / 2,
          r: (size - inset * 2) / 2,
          strokeWidth: stroke,
        },
      };
  }
}
